import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import { withTheme } from 'emotion-theming';
import find from 'lodash/find';
import get from 'lodash/get';

import MediaDisplay from '../../Common/MediaDisplay';
import BaseTextContainer from '../../Slats/SlatComponents/BaseTextContainer';

import LandingPageThemes from '../../../styling/themes/LandingPageThemes';
import { PadComponent } from '../../../styling/layout-containers/LandingPageContainers';
import colors from '../../../styling/colors';
import {
  MOBILE_END_QUERY,
  DESKTOP_START_QUERY,
  DESKTOP_MEDIUM_START_QUERY,
  DESKTOP_LARGE_START_QUERY,
  DESKTOP_BELOW_MAX_QUERY,
  DESKTOP_ABOVE_MAX_QUERY,
} from '../../../styling/mixins/responsive';

import withTrackingPropsProvider from '../../../hocs/withTrackingPropsProvider';

const SlatWrapper = styled.div`
  margin: 1.5rem 1.5rem 0;

  @media (${DESKTOP_START_QUERY}) and (${DESKTOP_BELOW_MAX_QUERY}) {
    ${({ theme, isIndented }) =>
      isIndented ? PadComponent(theme) : undefined};
  }
  ${({ theme }) => get(theme, 'Slat.marginBottomCss', ``)};

  min-height: 70vh;

  @media (min-aspect-ratio: 1/1) {
    min-height: 30rem;
    height: calc(102.5vh - ${({ topOffset }) => topOffset}px);
  }
`;

const FullSizeMediaDisplay = styled(MediaDisplay)`
  position: absolute;
  left: 0;
  right: 0;
  z-index: -1;
  padding: 0 1.5rem;
  min-height: 70vh;

  & .video-player {
    object-position: center top;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    padding: 0 1.5rem;
  }

  @media (min-aspect-ratio: 1/1) {
    .video-container& .video-player,
    .video-container& .video-poster {
      object-position: center bottom;
    }

    & .video-player {
      position: relative;
      top: auto;
      bottom: auto;
      padding: 0;
    }

    min-height: 30rem;
    height: calc(102.5vh - ${({ topOffset }) => topOffset}px);
  }
`;

const ChildContainer = styled.div`
  display: flex;
  justify-content: center;
  flex: 1 1 auto;
  color: white;
  flex-direction: column;
`;

const SpacerDiv = styled.div`
  flex: 0 0 auto;
  margin-bottom: 2vw;
  width: 45vw;
`;

const AspectRatioDiv = styled.div`
  padding-bottom: 55%;

  @media (max-aspect-ratio: 1/1) {
    min-height: 35vh;
  }
`;

const FixedWidthTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 45rem;
  text-align: center;
  margin: 0 auto;
  height: 100%;

  @media (max-aspect-ratio: 1/1) {
    min-height: 70vh;
  }
`;

const FullStretchSlat = ({
  className,
  id,
  layout,
  mobileLayout,
  content,
  media,
  theme,
  children,
  alignment,
  mobileAlignment,
  customContentStyles,
  contentColumnSizing,
}) => {
  const [topOffset, setTopOffset] = useState(120);
  const heroRef = useRef(null);
  useEffect(() => {
    setTopOffset(heroRef.current.offsetTop - 24);
  });

  return (
    <SlatWrapper
      id={id}
      ref={heroRef}
      className={className}
      topOffset={topOffset}
      theme={theme}
    >
      <FullSizeMediaDisplay media={media} topOffset={topOffset} />
      <FixedWidthTextContainer>
        <ChildContainer>{children}</ChildContainer>
        <SpacerDiv>
          <AspectRatioDiv />
        </SpacerDiv>
      </FixedWidthTextContainer>
    </SlatWrapper>
  );
};

FullStretchSlat.propTypes = {
  className: PropTypes.string,
  id: PropTypes.string,
  type: PropTypes.string,
  layout: PropTypes.oneOf(['left', 'right', 'center', 'custom']),
  mobileLayout: PropTypes.oneOf(['row', 'columnReverse']),
  content: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  media: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  tracking: PropTypes.object.isRequired,
};

FullStretchSlat.defaultProps = {
  color: 'lapel',
  layout: 'center',
  mobileLayout: 'row',
  theme: LandingPageThemes.default,
};

export default React.memo(withTrackingPropsProvider(FullStretchSlat));
